import type { ApiClient } from "../api";
import { friendlyMessage } from "../api";
import { loadIdentity, regenerateName, saveIdentity } from "../identity";
import { getInstallHint } from "../install";
import { ICONS, isCuratedIcon } from "../words";

export interface SettingsCtx {
  api: ApiClient;
  panelRoot: HTMLElement;
  notify: (msg: string) => void;
}

const NAME_MAX = 40;

export function openSettingsPanel(ctx: SettingsCtx): void {
  ctx.panelRoot.innerHTML = "";

  const ident = loadIdentity();

  const sheet = document.createElement("section");
  sheet.className = "panel sheet";
  sheet.setAttribute("role", "dialog");
  sheet.setAttribute("aria-label", "Settings");

  const closeBtn = document.createElement("button");
  closeBtn.type = "button";
  closeBtn.className = "panel-close";
  closeBtn.setAttribute("aria-label", "Close");
  closeBtn.textContent = "✕";
  closeBtn.addEventListener("click", () => {
    ctx.panelRoot.innerHTML = "";
  });
  sheet.appendChild(closeBtn);

  const h = document.createElement("h2");
  h.textContent = "You";
  sheet.appendChild(h);

  // Display name
  const nameLabel = document.createElement("label");
  nameLabel.textContent = "Display name";
  const nameRow = document.createElement("div");
  nameRow.className = "row";
  const nameInput = document.createElement("input");
  nameInput.type = "text";
  nameInput.maxLength = NAME_MAX;
  nameInput.value = ident.name;
  const shuffleBtn = document.createElement("button");
  shuffleBtn.type = "button";
  shuffleBtn.className = "btn-secondary";
  shuffleBtn.textContent = "Shuffle";
  shuffleBtn.addEventListener("click", () => {
    nameInput.value = regenerateName();
    counter.textContent = `${[...nameInput.value].length} / ${NAME_MAX}`;
  });
  const counter = document.createElement("span");
  counter.className = "counter";
  counter.textContent = `${[...nameInput.value].length} / ${NAME_MAX}`;
  nameInput.addEventListener("input", () => {
    counter.textContent = `${[...nameInput.value].length} / ${NAME_MAX}`;
  });
  nameRow.appendChild(nameInput);
  nameRow.appendChild(shuffleBtn);
  nameLabel.appendChild(nameRow);
  nameLabel.appendChild(counter);
  sheet.appendChild(nameLabel);

  // Icon picker
  const iconLabel = document.createElement("p");
  iconLabel.textContent = "Icon";
  sheet.appendChild(iconLabel);

  const iconPicker = document.createElement("div");
  iconPicker.className = "icon-picker";
  let selectedIcon = ident.icon;
  for (const ic of ICONS) {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "icon-btn";
    btn.textContent = ic;
    if (ic === selectedIcon) btn.classList.add("selected");
    btn.addEventListener("click", () => {
      selectedIcon = ic;
      iconPicker.querySelectorAll(".icon-btn.selected").forEach((el) => {
        el.classList.remove("selected");
      });
      btn.classList.add("selected");
    });
    iconPicker.appendChild(btn);
  }
  sheet.appendChild(iconPicker);

  // Save
  const save = document.createElement("button");
  save.type = "button";
  save.className = "btn-primary";
  save.textContent = "Save";
  sheet.appendChild(save);

  // Install-on-phone hint (no nag if already standalone)
  const hint = getInstallHint();
  if (hint.kind === "ios-share-sheet") {
    const note = document.createElement("p");
    note.className = "muted install-hint";
    note.textContent =
      "Tap the Share icon, then 'Add to Home Screen' to keep wheretopee one tap away.";
    sheet.appendChild(note);
  } else if (hint.kind === "android-prompt") {
    const installBtn = document.createElement("button");
    installBtn.type = "button";
    installBtn.className = "btn-secondary install-btn";
    installBtn.textContent = "Install app";
    installBtn.addEventListener("click", async () => {
      installBtn.disabled = true;
      try {
        const outcome = await hint.trigger();
        if (outcome === "accepted") {
          installBtn.remove();
          ctx.notify("installed - check your home screen");
        } else {
          installBtn.disabled = false;
        }
      } catch (err) {
        ctx.notify(friendlyMessage(err));
        installBtn.disabled = false;
      }
    });
    sheet.appendChild(installBtn);
  }

  // Disclaimer
  const disclaimer = document.createElement("details");
  disclaimer.className = "muted";
  const summary = document.createElement("summary");
  summary.textContent = "Privacy and identity";
  disclaimer.appendChild(summary);
  const body = document.createElement("p");
  body.textContent =
    "Your identity, your visits, and your visit type (pee/poo) live only in this browser. " +
    "Clearing site data or switching browsers creates a new identity that requires a fresh " +
    "verification on its first contribution. Personal pins are visible only to you.";
  disclaimer.appendChild(body);
  sheet.appendChild(disclaimer);

  ctx.panelRoot.appendChild(sheet);

  save.addEventListener("click", async () => {
    const newName = nameInput.value.trim();
    if (newName.length === 0 || [...newName].length > NAME_MAX) {
      ctx.notify("name must be 1 to 40 characters");
      return;
    }
    if (!isCuratedIcon(selectedIcon)) {
      ctx.notify("pick an icon");
      return;
    }
    save.disabled = true;
    saveIdentity({ id: ident.id, name: newName, icon: selectedIcon, version: 1 });
    try {
      await ctx.api.patchMe({ display_name: newName, icon: selectedIcon });
      ctx.notify("Saved.");
      ctx.panelRoot.innerHTML = "";
    } catch (err) {
      ctx.notify(friendlyMessage(err));
      save.disabled = false;
    }
  });
}
